import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Contacts.css';

interface Contact {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  country?: string;
}

interface Project {
  _id: string;
  name: string;
}

interface PortalAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contactId: string, projectIds: string[]) => void;
  workspaceId: string;
  existingAdmin?: {
    _id: string;
    contactId: string;
    displayName: string;
    projectIds: string[];
  };
}

const PortalAdminModal: React.FC<PortalAdminModalProps> = ({
  isOpen,
  onClose,
  onSave,
  workspaceId,
  existingAdmin
}) => {
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Load projects when modal opens
  useEffect(() => {
    if (isOpen && workspaceId) {
      fetchProjects();
    }
  }, [isOpen, workspaceId]);

  // Search contacts with debounce
  useEffect(() => {
    if (contactSearchQuery.trim() && !selectedContact) {
      // Clear existing timeout
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      // Set new timeout
      searchDebounceRef.current = setTimeout(() => {
        searchContacts();
      }, 300);

      return () => {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
      };
    } else if (!contactSearchQuery.trim()) {
      setContactSearchResults([]);
      setShowContactDropdown(false);
    }
  }, [contactSearchQuery, selectedContact]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setContactSearchQuery('');
      setContactSearchResults([]);
      setSelectedContact(null);
      setShowContactDropdown(false);
      setSelectedProjectIds([]);
    }
  }, [isOpen]);

  // Load existing admin data if editing
  useEffect(() => {
    if (isOpen && existingAdmin) {
      // Parse displayName to get first and last name
      const nameParts = existingAdmin.displayName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setSelectedContact({
        _id: existingAdmin.contactId,
        firstName: firstName,
        lastName: lastName,
        email: '',
      });
      setSelectedProjectIds(existingAdmin.projectIds);
      setContactSearchQuery(existingAdmin.displayName);
    }
  }, [isOpen, existingAdmin]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects || []);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const searchContacts = async () => {
    if (!workspaceId || !contactSearchQuery.trim()) return;

    try {
      setLoadingContacts(true);
      const response = await api.get(`/workspaces/${workspaceId}/contacts`, {
        params: {
          search: contactSearchQuery.trim(),
          limit: 20
        }
      });

      setContactSearchResults(response.data.contacts || []);
      setShowContactDropdown(true);
    } catch (err: any) {
      console.error('Error searching contacts:', err);
      toast.error('Failed to search contacts');
      setContactSearchResults([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleContactSelect = (contact: Contact) => {
    setSelectedContact(contact);
    setContactSearchQuery(`${contact.firstName} ${contact.lastName}`);
    setShowContactDropdown(false);
    setContactSearchResults([]);
  };

  const handleRemoveContact = () => {
    setSelectedContact(null);
    setContactSearchQuery('');
  };

  const handleToggleProject = (projectId: string) => {
    setSelectedProjectIds(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleSelectAllProjects = () => {
    if (selectedProjectIds.length === projects.length) {
      setSelectedProjectIds([]);
    } else {
      setSelectedProjectIds(projects.map(p => p._id));
    }
  };

  const handleSave = () => {
    if (!selectedContact) {
      toast.error('Please select a contact');
      return;
    }

    if (selectedProjectIds.length === 0) {
      toast.error('Please select at least one project');
      return;
    }

    onSave(selectedContact._id, selectedProjectIds);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div className="contacts-modal-overlay" onClick={onClose}>
        <div className="contacts-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
          {/* Modal Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 className="contacts-modal-title" style={{ margin: 0 }}>
              {existingAdmin ? 'Edit' : 'Add'} Purchaser Portal Admin
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#6B7280'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Modal Content */}
          <div style={{ marginBottom: '24px' }}>
            {/* Contact Search */}
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label className="form-label">
                Search for a contact <span style={{ color: '#DC2626' }}>*</span>
              </label>

              {/* Selected Contact */}
              {selectedContact && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  background: '#F3F4F6',
                  borderRadius: '6px',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    {selectedContact.firstName} {selectedContact.lastName}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveContact}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#6B7280'
                    }}
                    disabled={!!existingAdmin}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              {/* Search Input */}
              {!selectedContact && (
                <div style={{ position: 'relative', display: 'flex', gap: '8px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search by name or email..."
                      value={contactSearchQuery}
                      onChange={(e) => setContactSearchQuery(e.target.value)}
                      onFocus={() => {
                        if (contactSearchResults.length > 0) {
                          setShowContactDropdown(true);
                        }
                      }}
                    />
                    {loadingContacts && (
                      <div style={{ position: 'absolute', right: '12px', top: '12px' }}>
                        <div className="builders-loading-spinner" style={{ width: '16px', height: '16px' }} />
                      </div>
                    )}

                    {/* Contact Dropdown */}
                    {showContactDropdown && contactSearchResults.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          marginTop: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 10,
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {contactSearchResults.map((contact) => (
                          <div
                            key={contact._id}
                            onClick={() => handleContactSelect(contact)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #F3F4F6',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            <div style={{ fontWeight: '500', fontSize: '14px', marginBottom: '2px' }}>
                              {contact.firstName} {contact.lastName}
                            </div>
                            <div style={{ fontSize: '13px', color: '#6B7280' }}>
                              {contact.email}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Project Selection */}
            <div className="form-group">
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Project(s) <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <button
                  type="button"
                  onClick={handleSelectAllProjects}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#48182F',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  {selectedProjectIds.length === projects.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div style={{
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '8px'
              }}>
                {loadingProjects ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <div className="builders-loading-spinner" />
                  </div>
                ) : projects.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
                    No projects available
                  </div>
                ) : (
                  projects.map((project) => (
                    <label
                      key={project._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProjectIds.includes(project._id)}
                        onChange={() => handleToggleProject(project._id)}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px' }}>{project.name}</span>
                    </label>
                  ))
                )}
              </div>

              {selectedProjectIds.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#6B7280' }}>
                  {selectedProjectIds.length} project{selectedProjectIds.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          </div>

          {/* Modal Actions */}
          <div className="contacts-modal-actions">
            <button
              className="btn btn-secondary btn-md"
              onClick={onClose}
              disabled={saving}
            >
              Close
            </button>
            <button
              className="btn btn-primary btn-md"
              onClick={handleSave}
              disabled={saving || !selectedContact || selectedProjectIds.length === 0}
            >
              {saving ? 'Saving...' : existingAdmin ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PortalAdminModal;
